// src/components/Attendance/LiveKioskSection.js (PERFORMANCE & SECURITY ENHANCED - FULL CODE)

import React, { useState, useEffect, useRef } from 'react';
import { useAppContext } from '../../App';
import { useNavigate } from 'react-router-dom';
import { api } from '../../apiService';
import { toast } from 'react-toastify';

const LiveKioskSection = ({ setActiveTab }) => {
    const navigate = useNavigate();
    const { allStations, currentStationId } = useAppContext();
    
    const videoRef = useRef(null);
    const [employees, setEmployees] = useState([]);
    const [logData, setLogData] = useState([]);
    const [statusText, setStatusText] = useState('Camera is Offline');
    const [isCameraOn, setIsCameraOn] = useState(false);
    const [stream, setStream] = useState(null);
    const [recognizedEmployee, setRecognizedEmployee] = useState(null);
    const [labeledDescriptors, setLabeledDescriptors] = useState(null);
    const [modelsLoaded, setModelsLoaded] = useState(false);
    const detectionInterval = useRef(null);
    const [time, setTime] = useState(new Date());
    const [currentLocation, setCurrentLocation] = useState({ status: 'idle', data: null });
    
    // --- SECURITY & UX STATE ---
    const [isProcessing, setIsProcessing] = useState(false); // For button loading state
    const [detectionConfidence, setDetectionConfidence] = useState(0); // For liveness confidence meter
    const confidenceThreshold = 5; // Require 5 successful detections
    const confidenceCounter = useRef(0);

    const toYYYYMMDD = (date) => {
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
    };
    
    const [filterDate, setFilterDate] = useState(toYYYYMMDD(new Date()));

    useEffect(() => {
        const timerId = setInterval(() => setTime(new Date()), 1000);
        return () => clearInterval(timerId);
    }, []);
    
    useEffect(() => {
        const loadModels = async () => {
            if (modelsLoaded || !window.faceapi) return;
            setStatusText("Loading AI Models...");
            try {
                const MODEL_URL = 'https://cdn.jsdelivr.net/gh/justadudewhohacks/face-api.js@0.22.2/weights';
                await Promise.all([
                    window.faceapi.nets.tinyFaceDetector.loadFromUri(MODEL_URL),
                    window.faceapi.nets.faceLandmark68Net.loadFromUri(MODEL_URL),
                    window.faceapi.nets.faceRecognitionNet.loadFromUri(MODEL_URL),
                ]);
                setModelsLoaded(true);
                setStatusText('Camera is Offline. Press Start.');
            } catch (e) {
                setStatusText('Error: Failed to load AI models.');
                console.error("Error loading models:", e); toast.error("AI models could not be loaded.");
            }
        };
        if (window.faceapi) { loadModels(); } else { const timer = setTimeout(() => { if(window.faceapi) loadModels(); else toast.error("FaceAPI script not found."); }, 1000); return () => clearTimeout(timer); }
    }, [modelsLoaded]); 

    useEffect(() => {
        if (!modelsLoaded || !currentStationId || currentStationId === 'all') { setLabeledDescriptors(null); if (currentStationId === 'all') setStatusText('Please select a station to start the kiosk.'); return; }
        setStatusText("Loading employee face data...");
        const fetchEmployees = async () => {
            try {
                const fetchedEmployees = await api.get('workers', { where: { stationId: currentStationId } });
                setEmployees(fetchedEmployees);
                setLabeledDescriptors(fetchedEmployees.map(emp => emp.faceDescriptor && emp.faceDescriptor.length > 0 ? new window.faceapi.LabeledFaceDescriptors(emp.employeeId, [new Float32Array(emp.faceDescriptor)]) : null).filter(Boolean));
                setStatusText(isCameraOn ? 'System Ready.' : 'Employee data loaded. Ready to start camera.');
            } catch (error) {
                console.error("Error fetching employees for Kiosk:", error);
                toast.error("Could not process employee face data.");
            }
        };
        fetchEmployees();
    }, [modelsLoaded, currentStationId, isCameraOn]);
    
    useEffect(() => {
        if(!currentStationId || currentStationId === 'all') { setLogData([]); return; }
        const fetchLogData = async () => {
            try {
                const data = await api.get('attendance', { where: { date: filterDate, stationId: currentStationId }, orderBy: 'checkIn DESC' });
                setLogData(data);
            } catch (err) {
                console.error("Attendance Log Error:", err);
                toast.error("Could not fetch attendance log.");
            }
        };
        fetchLogData();
    }, [filterDate, currentStationId]);
    
    useEffect(() => {
        return () => { if (stream) stream.getTracks().forEach(track => track.stop()); if (detectionInterval.current) clearInterval(detectionInterval.current); };
    }, [stream]);
    
    const getLocation = () => {
        setCurrentLocation({ status: 'fetching', data: null });
        return new Promise((resolve, reject) => {
            if (!navigator.geolocation) { setCurrentLocation({ status: 'error', data: 'Geolocation is not supported.' }); return reject(new Error("Geolocation is not supported.")); }
            navigator.geolocation.getCurrentPosition( async (position) => { const { latitude, longitude } = position.coords; try { const response = await fetch(`https://nominatim.openstreetmap.org/reverse?format=json&lat=${latitude}&lon=${longitude}`); const data = await response.json(); const address = data.address || {}; const locationHierarchy = [ address.shop, address.amenity, address.building, address.road, address.neighbourhood, address.suburb, address.quarter, address.city, address.town, address.village ]; const mostSpecificName = locationHierarchy.find(name => name) || 'Unknown Area'; const cityName = address.city || address.town || address.village || ''; const displayName = (mostSpecificName !== cityName && cityName) ? `${mostSpecificName}, ${cityName}` : mostSpecificName; const locationData = { latitude, longitude, name: displayName }; setCurrentLocation({ status: 'success', data: locationData }); resolve(locationData); } catch (apiError) { const locationData = { latitude, longitude, name: 'Coordinates Acquired (Name not found)' }; setCurrentLocation({ status: 'success', data: locationData }); resolve(locationData); } }, (error) => { setCurrentLocation({ status: 'error', data: error.message }); reject(error); } );
        });
    };

    const startDetection = () => { 
        if (!labeledDescriptors || labeledDescriptors.length === 0) { 
            setStatusText("No employee faces registered for this station."); 
            return; 
        } 
        const faceMatcher = new window.faceapi.FaceMatcher(labeledDescriptors, 0.5); 
        
        detectionInterval.current = setInterval(async () => { 
            if (!videoRef.current || recognizedEmployee || videoRef.current.paused || videoRef.current.ended) return; 
            setStatusText("Searching for faces..."); 
            const detection = await window.faceapi.detectSingleFace(videoRef.current, new window.faceapi.TinyFaceDetectorOptions()).withFaceLandmarks().withFaceDescriptor();
            
            if (detection) { 
                const result = faceMatcher.findBestMatch(detection.descriptor); 
                
                if (result.label !== 'unknown') { 
                    setStatusText(`Face found: ${result.label}. Please hold still.`);
                    confidenceCounter.current++;
                    const newConfidence = Math.min(100, (confidenceCounter.current / confidenceThreshold) * 100);
                    setDetectionConfidence(newConfidence);

                    if (confidenceCounter.current >= confidenceThreshold) {
                        const employee = employees.find(e => e.employeeId === result.label); 
                        if (employee) { 
                            setRecognizedEmployee(employee); 
                            setStatusText(`Recognized: ${employee.fullName}`); 
                            clearInterval(detectionInterval.current); 
                        }
                    }
                } else { 
                    setStatusText("Unknown person detected. Resetting..."); 
                    confidenceCounter.current = 0;
                    setDetectionConfidence(0);
                } 
            } else { 
                setStatusText("No face detected. Please position your face in the camera."); 
                confidenceCounter.current = 0;
                setDetectionConfidence(0);
            } 
        }, 1200);
    };
    
    const handleVideoPlay = () => { 
        setStatusText('System Ready. Please look at the camera.'); 
        startDetection(); 
    };

    const startWebcam = async () => { 
        if (!modelsLoaded || !labeledDescriptors) { toast.error("AI Models or employee data not loaded."); return; } 
        if (stream) stream.getTracks().forEach(track => track.stop()); 
        setStatusText('Requesting camera permission...'); 
        try { 
            const newStream = await navigator.mediaDevices.getUserMedia({ video: true }); 
            if (videoRef.current) videoRef.current.srcObject = newStream; 
            setStream(newStream); setIsCameraOn(true); 
            await getLocation(); 
        } catch (err) { 
            setStatusText('Webcam/Location Access Denied.'); setIsCameraOn(false); 
        } 
    };

    const resetKiosk = () => {
        setRecognizedEmployee(null);
        setDetectionConfidence(0);
        confidenceCounter.current = 0;
        if (isCameraOn) {
            setStatusText('System Ready. Please look at the camera.');
            startDetection();
        }
    };

    const stopWebcam = () => { 
        if (stream) stream.getTracks().forEach(track => track.stop()); 
        if (videoRef.current) videoRef.current.srcObject = null; 
        setStream(null); setStatusText('Camera is Offline.'); setIsCameraOn(false); 
        clearInterval(detectionInterval.current); 
        setCurrentLocation({ status: 'idle', data: null });
        resetKiosk();
    };

    const handleAttendanceAction = async (action) => {
        const employee = recognizedEmployee;
        if (!employee || isProcessing) return;

        setIsProcessing(true);
        
        let locationData;
        try {
            locationData = await getLocation();
        } catch (locError) {
            toast.error("Could not get your location. Please enable location services and try again.");
            setIsProcessing(false);
            return;
        }

        const now = new Date();
        const todayStr = toYYYYMMDD(now);
        const employeeIdKeys = [employee.employeeId, employee.id].filter(Boolean);
        
        try {
            if (action === 'check-in') {
                const existingLogs = await api.get('attendance', { where: { date: todayStr, employeeId: { in: employeeIdKeys } } });
    
                if (existingLogs.length > 0) {
                    toast.error(`${employee.fullName} has already checked in today.`);
                } else {
                    let status = 'Present';
                    const shiftStartTime = employee.dutyShifts?.[0]?.start;
                    if (shiftStartTime) {
                        const [shiftHour, shiftMinute] = shiftStartTime.split(':').map(Number);
                        if (now.getHours() > shiftHour || (now.getHours() === shiftHour && now.getMinutes() > shiftMinute)) { status = 'Late'; }
                    }
                    await api.post('attendance', { employeeId: employee.employeeId, fullName: employee.fullName, photoURL: employee.photoURL, stationId: employee.stationId, date: todayStr, checkIn: now.toISOString(), checkOut: null, status, checkInLocation: locationData });
                    toast.success(`${employee.fullName} checked in at ${locationData.name}`);
                }
            } else { 
                const yesterday = new Date(now); yesterday.setDate(now.getDate() - 1);
                const yesterdayStr = toYYYYMMDD(yesterday);
                const shiftCutoffHour = 12;

                const [todayLogs, yesterdayLogs] = await Promise.all([
                    api.get('attendance', { where: { date: todayStr, employeeId: { in: employeeIdKeys } } }),
                    api.get('attendance', { where: { date: yesterdayStr, employeeId: { in: employeeIdKeys } } })
                ]);

                let logToUpdate = null;
                if (todayLogs.length > 0 && !todayLogs[0].checkOut) { logToUpdate = todayLogs[0]; } 
                else if (now.getHours() < shiftCutoffHour && yesterdayLogs.length > 0 && !yesterdayLogs[0].checkOut) { logToUpdate = yesterdayLogs[0]; }
    
                if (logToUpdate) {
                    await api.put('attendance', logToUpdate.id, { checkOut: now.toISOString(), status: 'Completed', checkOutLocation: locationData });
                    toast.success(`${employee.fullName} checked out successfully from ${locationData.name}`);
                } else {
                    if (todayLogs.length > 0 && todayLogs[0].checkOut) { toast.error(`${employee.fullName} has already checked out today.`); } 
                    else if (yesterdayLogs.length > 0 && yesterdayLogs[0].checkOut) { toast.error(`${employee.fullName} already checked out for the previous shift.`); } 
                    else { toast.error(`${employee.fullName} has not checked in yet.`); }
                }
            }
        } catch (err) {
            console.error("Attendance Action Error:", err);
            toast.error("Attendance marking failed. Please try again.");
        } finally {
            setIsProcessing(false);
            resetKiosk();
        }
    };
    
    const isCheckedIn = recognizedEmployee ? logData.some(log => (log.employeeId === recognizedEmployee.employeeId || log.employeeId === recognizedEmployee.id) && log.checkIn && !log.checkOut) : false;
    const isCheckedOut = recognizedEmployee ? logData.some(log => (log.employeeId === recognizedEmployee.employeeId || log.employeeId === recognizedEmployee.id) && log.checkOut) : false;
    
    const summary = { 
        total: employees.length, 
        present: logData.filter(e => ['Present', 'Late', 'Completed'].includes(e.status)).length, 
        late: logData.filter(e => e.status === 'Late').length, 
        onLeave: logData.filter(e => e.status === 'On Leave').length, 
        absent: employees.length - logData.filter(e => e.status !== 'Absent').length, 
    };

    return (
        <div className="container mx-auto">
            <div className="mb-8 flex justify-between items-center">
                <div>
                    <h1 className="text-4xl font-bold text-gray-800">Live Attendance Kiosk</h1>
                    <p className="text-gray-500 mt-1">For: <span className="font-bold text-rose-600">{currentStationId === 'all' ? 'All Stations' : allStations.find(s => s.id === currentStationId)?.name}</span></p>
                </div>
                <div className="text-right">
                    <p className="text-4xl font-bold text-rose-600">{time.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: true })}</p>
                    <p className="text-base text-gray-500">{time.toLocaleDateString('en-US', { weekday: 'long', day: 'numeric', month: 'long' })}</p>
                </div>
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 items-start">
                <div className="lg:col-span-2 bg-white p-6 rounded-2xl shadow-lg flex flex-col">
                    <div className="relative w-full aspect-video bg-black rounded-xl overflow-hidden">
                        <video ref={videoRef} onPlay={handleVideoPlay} autoPlay muted playsInline className="w-full h-full object-cover transform scale-x-[-1]"></video>
                        
                        {!recognizedEmployee && isCameraOn && detectionConfidence > 0 && (
                            <div className="absolute bottom-4 left-4 right-4 px-2">
                                <div className="w-full bg-gray-600 rounded-full h-2.5">
                                    <div className="bg-green-500 h-2.5 rounded-full" style={{ width: `${detectionConfidence}%`, transition: 'width 0.5s ease-in-out' }}></div>
                                </div>
                            </div>
                        )}

                        {recognizedEmployee && (
                            <div className="absolute inset-0 p-6 bg-gradient-to-t from-black/80 to-transparent flex items-end justify-between">
                                <div className="flex items-center gap-4">
                                    <img src={recognizedEmployee.photoURL} className="w-24 h-24 rounded-full border-4 border-white shadow-lg" alt={recognizedEmployee.fullName} />
                                    <div>
                                        <p className="text-lg text-white">Welcome</p>
                                        <p className="text-3xl font-bold text-white">{recognizedEmployee.fullName}</p>
                                        <div className="mt-4 flex gap-4">
                                            <button onClick={() => handleAttendanceAction('check-in')} disabled={isCheckedIn || isCheckedOut || isProcessing} className="bg-green-500 hover:bg-green-600 text-white font-bold py-2 px-6 rounded-lg disabled:bg-gray-400 w-32 text-center">
                                                {isProcessing ? 'Wait...' : 'Check-In'}
                                            </button>
                                            <button onClick={() => handleAttendanceAction('check-out')} disabled={!isCheckedIn || isCheckedOut || isProcessing} className="bg-red-500 hover:bg-red-600 text-white font-bold py-2 px-6 rounded-lg disabled:bg-gray-400 w-32 text-center">
                                                {isProcessing ? 'Wait...' : 'Check-Out'}
                                            </button>
                                        </div>
                                    </div>
                                </div>
                                <button onClick={resetKiosk} disabled={isProcessing} className="self-start p-2 bg-white/20 text-white rounded-full hover:bg-white/40">
                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor"><path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" /></svg>
                                </button>
                            </div>
                        )}
                    </div>
                    <div className="mt-4 bg-gray-100 px-6 py-3 rounded-lg flex items-center gap-4">
                        <div className={`w-3.5 h-3.5 rounded-full ${isCameraOn ? 'bg-green-500 animate-pulse' : 'bg-red-500'}`}></div>
                        <p className="text-lg font-medium">{statusText}</p>
                    </div>
                </div>
                <div className="lg:col-span-1 space-y-6">
                    <div className="bg-white p-6 rounded-2xl shadow-lg">
                        <h3 className="font-bold text-lg text-gray-700 mb-4">System Controls</h3>
                        <div className="space-y-4">
                            {!isCameraOn ? 
                                <button onClick={startWebcam} disabled={!modelsLoaded || currentStationId === 'all'} className="w-full bg-rose-600 text-white font-bold py-3 px-4 rounded-lg disabled:bg-gray-400">{currentStationId === 'all' ? 'Select Station First' : modelsLoaded ? 'Start Camera' : 'Loading Models...'}</button> : 
                                <button onClick={stopWebcam} className="w-full bg-red-600 text-white font-bold py-3 px-4 rounded-lg">Stop Camera</button>
                            }
                        </div>
                        <div className="mt-4 pt-4 border-t border-gray-200">
                            <h4 className="font-bold text-sm text-gray-700 mb-2">Current Location</h4>
                            <div className="flex items-start gap-3">
                                <span className="text-blue-500 mt-1">📍</span>
                                <div>
                                    {currentLocation.status === 'success' && currentLocation.data ? (<><p className="text-sm font-semibold text-gray-800">{currentLocation.data.name}</p><p className="text-xs text-gray-500">Lat: {currentLocation.data.latitude.toFixed(4)}, Lon: {currentLocation.data.longitude.toFixed(4)}</p></>) 
                                    : currentLocation.status === 'fetching' ? (<p className="text-xs text-gray-500 italic">Fetching location...</p>) 
                                    : (<p className="text-xs text-red-500">Location not available.</p>)}
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="bg-white p-6 rounded-2xl shadow-lg">
                        <h3 className="font-bold text-lg text-gray-700 mb-4">Today's Summary</h3>
                        <div className="grid grid-cols-2 gap-4">
                            <div className="bg-gradient-to-br from-green-500 to-green-600 text-white p-4 rounded-lg text-center"><p className="text-sm font-semibold">Present</p><p className="text-3xl font-extrabold">{summary.present}</p></div>
                            <div className="bg-gradient-to-br from-red-500 to-red-600 text-white p-4 rounded-lg text-center"><p className="text-sm font-semibold">Absent</p><p className="text-3xl font-extrabold">{summary.absent < 0 ? 0 : summary.absent}</p></div>
                            <div className="bg-gradient-to-br from-yellow-500 to-yellow-600 text-white p-4 rounded-lg text-center"><p className="text-sm font-semibold">Late</p><p className="text-3xl font-extrabold">{summary.late}</p></div>
                            <div className="bg-gradient-to-br from-blue-500 to-blue-600 text-white p-4 rounded-lg text-center"><p className="text-sm font-semibold">On Leave</p><p className="text-3xl font-extrabold">{summary.onLeave}</p></div>
                        </div>
                        <div className="mt-6 pt-6 border-t border-gray-200">
                            <button onClick={() => navigate('/monthlyReport')} className="w-full bg-gray-600 text-white font-bold py-3 px-4 rounded-lg shadow-lg hover:bg-gray-700 transition-all">View Monthly Report</button>
                        </div>
                    </div>
                </div>
            </div>
            <div className="bg-white rounded-2xl shadow-lg overflow-hidden mt-8">
                <div className="p-6 border-b flex justify-between items-center">
                    <h2 className="text-2xl font-bold text-gray-800">Attendance Log</h2>
                    <input type="date" value={filterDate} onChange={e => setFilterDate(e.target.value)} className="bg-gray-50 border-gray-300 rounded-md shadow-sm p-2 font-semibold"/>
                </div>
                <div className="overflow-x-auto">
                    <table className="w-full text-sm text-left text-gray-500">
                        <thead className="text-xs text-gray-700 uppercase bg-gray-50">
                            <tr>
                                <th className="px-6 py-3">Employee</th>
                                <th className="px-6 py-3">Check-in</th>
                                <th className="px-6 py-3">Check-out</th>
                                <th className="px-6 py-3">Location</th>
                                <th className="px-6 py-3 text-center">Status</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-200">
                            {logData.length > 0 ? logData.map(log => (
                                <tr key={log.id}>
                                    <td className="px-6 py-4 flex items-center gap-3">
                                        <img src={log.photoURL} className="w-10 h-10 rounded-full object-cover" alt={log.fullName} />
                                        <div>
                                            <div className="font-semibold text-gray-800">{log.fullName}</div>
                                            <div className="text-xs text-gray-500">{allStations.find(s => s.id === log.stationId)?.name || 'N/A'}</div>
                                        </div>
                                    </td>
                                    <td className="px-6 py-4">{log.checkIn ? new Date(log.checkIn).toLocaleTimeString() : '--:--'}</td>
                                    <td className="px-6 py-4">{log.checkOut ? new Date(log.checkOut).toLocaleTimeString() : '--:--'}</td>
                                    <td className="px-6 py-4 text-xs text-gray-600">
                                        <div className="relative group">
                                            <span className="cursor-pointer">{log.checkInLocation?.name || 'N/A'}</span>
                                            {log.checkInLocation && (
                                                <div className="absolute bottom-full mb-2 hidden group-hover:block w-max bg-gray-800 text-white text-xs rounded py-1 px-2 z-10">
                                                    Lat: {log.checkInLocation.latitude.toFixed(4)}, Lon: {log.checkInLocation.longitude.toFixed(4)}
                                                    <div className="absolute top-full left-1/2 -translate-x-1/2 w-0 h-0 border-x-4 border-x-transparent border-t-4 border-t-gray-800"></div>
                                                </div>
                                            )}
                                        </div>
                                    </td>
                                    <td className="px-6 py-4 text-center"><span className={`px-3 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${log.status === 'Completed' ? 'bg-blue-100 text-blue-800' : log.status === 'Late' ? 'bg-yellow-100 text-yellow-800' : 'bg-green-100 text-green-800'}`}>{log.status}</span></td>
                                </tr>
                            )) : <tr><td colSpan="5" className="text-center p-8 text-gray-500">No attendance records for this date.</td></tr>}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
};

export default LiveKioskSection;
